import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { authAPI } from '../utils/apiUtils';
import './LoginModal.css';
import { APP_NAME } from '../constants/appConstants';
// Analytics imports
import { trackEvent } from '../firebase/analytics';
import { ANALYTICS_EVENTS } from '../firebase/analyticsEvents';

const LoginModal = ({ isOpen, onClose, onLogin, context = 'general', onOpenTerms, onOpenPrivacy }) => {
  const [isLoading, setIsLoading] = useState(false);

  // Get appropriate message based on context
  const getModalMessage = () => {
    switch (context) {
      case 'chat':
        return 'Please sign in to start chatting with other users!';
      default:
        return 'Sign in to start chatting with friends!';
    }
  };

  const getModalTitle = () => {
    return 'Hey you\'re back';
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    trackEvent(ANALYTICS_EVENTS.LOGIN_BUTTON_CLICK, {
      login_method: 'google',
      context: context,
      timestamp: Date.now()
    });
    
    setIsLoading(true);
    try {
      // Print the raw JWT token for server verification
      console.log('=== GOOGLE JWT TOKEN ===');
      console.log('Raw JWT Token:', credentialResponse.credential);
      console.log('========================');
      
      // Decode the JWT token for local display
      const userInfo = parseJwt(credentialResponse.credential);
      console.log('Decoded User info:', userInfo);
      
      // Call your backend API to verify the token
      console.log('Calling backend API for token verification...');
      const apiResponse = await authAPI.googleSignIn(credentialResponse.credential);
      
      console.log('=== API RESPONSE ===');
      console.log('Backend response:', apiResponse);
      console.log('===================');
      
      // Use the backend response data
      if (onLogin) {
        onLogin({
          // Use data from your backend API response
          id: apiResponse.user.id,
          name: `${apiResponse.user.firstName} ${apiResponse.user.lastName}`,
          email: apiResponse.user.email,
          picture: apiResponse.user.profilePicture,
          username: apiResponse.user.username,
          authProvider: apiResponse.user.authProvider,
          // Store both tokens
          googleToken: credentialResponse.credential, // Original Google JWT
          backendToken: apiResponse.token, // Your backend JWT
          apiResponse: apiResponse // Full response for debugging
        });
        
        // Track successful login
        trackEvent(ANALYTICS_EVENTS.LOGIN_SUCCESS, {
          login_method: 'google',
          context: context,
          user_id: apiResponse.user.id,
          timestamp: Date.now()
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Error during Google sign-in:', error);
      
      // Show specific error messages
      if (error.message.includes('Failed to fetch')) {
        alert('Could not connect to the authentication server. Please check if the backend is running.');
      } else if (error.message.includes('API call failed')) {
        alert(`Authentication failed: ${error.message}`);
      } else {
        alert('Error processing sign-in. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google Sign-In failed:', error);
    
    // Provide specific error messages
    if (error?.error === 'invalid_client') {
      alert('Google Sign-In configuration error. Please check the Google OAuth configuration.');
    } else if (error?.error === 'popup_closed_by_user') {
      // User closed the popup, don't show error
      return;
    } else {
      alert('Google Sign-In failed. Please try again.');
    }
  };

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT:', e);
      return null;
    }
  };

  // Check if we have a valid Google Client ID
  const hasValidClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID && 
                          process.env.REACT_APP_GOOGLE_CLIENT_ID !== 'your-google-client-id-here';

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Verifying with server...</p>
          </div>
        )}
        
        <div className="login-modal-header">
          <div className="logo-container">
            <img src="/logo.svg" alt={APP_NAME} className="modal-logo" />
          </div>
          <h2>{getModalTitle()}</h2>
          <p className="modal-subtitle">{getModalMessage()}</p>
          <button className="close-button" onClick={() => {
            trackEvent(ANALYTICS_EVENTS.MODAL_CLOSE_CLICK, {
              modal_type: 'login',
              context: context,
              timestamp: Date.now()
            });
            onClose();
          }}>×</button>
        </div>
        
        <div className="login-modal-body">
          <div className="google-signin-container">
            {hasValidClientId ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: '16px' 
              }}>
                {/* Use GoogleLogin component directly */}
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                  theme="filled_blue"
                  width="280"
                />
              </div>
            ) : (
              <button className="google-signin-btn" disabled>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.68 8.18182C15.68 7.61455 15.6291 7.07273 15.5345 6.54545H8.16364V9.63636H12.5091C12.3018 10.64 11.6982 11.4836 10.8073 12.0509V14.0691H13.3527C14.9164 12.6691 15.68 10.6182 15.68 8.18182Z" fill="white"/>
                  <path d="M8.16364 16C10.3636 16 12.2036 15.2836 13.3527 14.0691L10.8073 12.0509C10.0836 12.5345 9.13091 12.8145 8.16364 12.8145C6.05091 12.8145 4.24727 11.4036 3.61091 9.52727H1.01818V11.5927C2.16364 13.8764 4.97818 16 8.16364 16Z" fill="white"/>
                  <path d="M3.61091 9.52727C3.43636 8.98182 3.43636 8.41455 3.61091 7.86909V5.80364H1.01818C0.374545 7.06909 0.374545 8.93091 1.01818 10.1964L3.61091 9.52727Z" fill="white"/>
                  <path d="M8.16364 3.18545C9.22545 3.16364 10.24 3.56364 11.0109 4.26909L13.2655 2.01455C12.1455 0.981818 10.6691 0.407273 8.16364 0.436364C4.97818 0.436364 2.16364 2.56 1.01818 4.84364L3.61091 6.90909C4.24727 5.03273 6.05091 3.18545 8.16364 3.18545Z" fill="white"/>
                </svg>
                Sign in with google (Configuration needed)
              </button>
            )}
            
            <p className="terms-notice">
              By logging in, you accept our{' '}
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  trackEvent(ANALYTICS_EVENTS.TOS_MODAL_OPEN, {
                    source: 'login_modal',
                    context: context,
                    timestamp: Date.now()
                  });
                  if (onOpenTerms) onOpenTerms();
                }}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  trackEvent(ANALYTICS_EVENTS.PRIVACY_MODAL_OPEN, {
                    source: 'login_modal',
                    context: context,
                    timestamp: Date.now()
                  });
                  if (onOpenPrivacy) onOpenPrivacy();
                }}
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="modal-decorations">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-line"></div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
